import { useSelector, useDispatch } from 'react-redux'
import { Modal, Button } from '@icanbwell/ui-toolkit'
import { useUserLogoutMutation, useSessionUpdateMutation } from '@client-hub/store/api/crs/react'
import { selectCrsToken } from '@client-hub/store/slices/metadata/selectors'
import { Close } from '@mui/icons-material'
import { useIdleTimer } from 'react-idle-timer'
import { Box, Typography, Fab } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FC, useState, useEffect } from 'react'
import { crsApi } from '@client-hub/store/api/crs/react'
import { ccsApi } from '@client-hub/store/api/ccs/react'

// Timer constants
const AUTO_LOGOUT = {
  TIMEOUT: 30 * 60 * 1000, // 30 minutes
  EVENTS_THROTTLE: 200,
  ACTIVITY_DEBOUNCE: 1 * 60 * 1000, // 1 minute
}
const LOGOUT_MODAL = {
  TIMEOUT: 30 * 1000, // 30 seconds
}

const HubSessionTimer: FC = () => {
  // hooks
  const token = useSelector(selectCrsToken)
  const { t } = useTranslation()

  const [crsLogout] = useUserLogoutMutation()
  const [sessionUpdate] = useSessionUpdateMutation()

  const [logoutModalOpened, setLogoutModalOpened] = useState(false)
  const [modalCountdown, setModalCountdown] = useState(LOGOUT_MODAL.TIMEOUT / 1000)

  const dispatch = useDispatch()

  /**
   * A timeout for the auto-logout modal
   */
  const {
    reset: logoutModalTimerReset,
    start: logoutModalTimerStart,
    pause: logoutModalTimerPause,
    getRemainingTime,
  } = useIdleTimer({
    onIdle: () => {
      crsLogout({ token: token! }).then(() => {
        // Reset related API(s)
        dispatch(ccsApi.util.resetApiState())
        dispatch(crsApi.util.resetApiState())
      })
    },
    /**
     * This timer is controlled by callbacks/ manual triggering
     */
    events: [],
    timeout: LOGOUT_MODAL.TIMEOUT,
    startOnMount: false,
    emitOnAllTabs: false,
  })

  /**
   * A timer for the entire application.
   * When logged in, the timer starts counting down AUTO_LOGOUT.TIMEOUT time. The timer works on all mfes.
   * Please note, that this implementation is a Front-End based timer. Although we synchronize session updates
   * with the Backend, the overall heavylifting (such as user inactivity detection) is handled on FE side.
   */
  useIdleTimer({
    onIdle: () => {
      logoutModalTimerStart()
      setLogoutModalOpened(true)
    },
    onActive: () => {
      logoutModalTimerStop()
    },
    timeout: AUTO_LOGOUT.TIMEOUT,
    eventsThrottle: AUTO_LOGOUT.TIMEOUT,
    /**
     * This event is allowed to fire as soon as the app loads
     */
    startOnMount: true,
    /**
     * All tabs have a different session of their own.
     */
    emitOnAllTabs: true,
    /**
     * The following 2 lines of code ensures that after all or any user activity at all, we update the session.
     * This is to prevent a situation where a user might be active but not making any API calls, in which case,
     * after 30 minutes, the Backend session shall expire.
     *
     * This enables us to fire an event after any activity happens on the FE side. Since it is debounced, it has
     * minimal performance issues. The countdown begins after the session is updated and the user is still idling.
     *
     * This approach comes with an inherent "vulnerability". If a user does not let go off of the activity and
     * doesnt allow the callback to fire, the user will eventually be greeted with a Backend session expire eventually.
     * But for our usecase (and to simplify our logic), we shall ignore that a user will block this event for 30 minutes straight.
     */
    debounce: AUTO_LOGOUT.ACTIVITY_DEBOUNCE,
    onAction: () => {
      sessionUpdate({ token: token! })
    },
    // A list of events that reset the timer
    events: ['mousemove', 'keydown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove'],
  })
  useEffect(() => {
    const interval = setInterval(() => {
      setModalCountdown(getRemainingTime())
    }, 1000)
    return () => clearInterval(interval)
  }, [getRemainingTime])

  // functions and callbacks

  // emulates stopping of the logout timer
  const logoutModalTimerStop = () => {
    logoutModalTimerReset()
    logoutModalTimerPause()
  }
  const regainedActivity = () => {
    sessionUpdate({ token: token! })
    logoutModalTimerStop()
    setLogoutModalOpened(false)
  }

  return (
    <Modal
      id="session-timeout-modal"
      handleClose={() => {
        regainedActivity()
      }}
      opened={logoutModalOpened}
      children={
        <Box
          sx={{
            height: '350px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Typography sx={{ fontSize: '1.5rem', marginTop: '100px' }}>
            <b>{t('authGuard.loggingYouOutin', { remaining: Math.floor(modalCountdown / 1000) })}</b>
          </Typography>
          <Box mt={3}>
            <Button
              text={t('authGuard.keepMeLoggedIn')}
              onClick={() => {
                regainedActivity()
              }}
            />
          </Box>
          <Box mt={3}>
            <Button
              text={t('authGuard.logout')}
              variant="outlined"
              onClick={() => {
                crsLogout({ token: token! }).then(() => {
                  // Reset related API(s)
                  dispatch(ccsApi.util.resetApiState())
                  dispatch(crsApi.util.resetApiState())
                })
              }}
            />
          </Box>
          <Fab
            aria-label="Close Button"
            sx={{ position: 'absolute', top: 20, right: 20 }}
            onClick={() => {
              regainedActivity()
            }}
          >
            <Close />
          </Fab>
        </Box>
      }
    />
  )
}

export default HubSessionTimer
